(function () {
// One delivery-date engine for PDP, Cuttlefish and checkout; invalid input gives null (no date); no imports so build-browser.mjs can strip `export`.

const ENGINE_VERSION = '0.1.0';

const DAY_MS = 86400000;

// Calendar days are day numbers (days since 1970-01-01) so DST and the shopper's timezone can't shift them.
function dayNumber(y, m, d) {
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

// 0 = Sunday … 6 = Saturday; 1970-01-01 was a Thursday.
function weekday(dn) {
  return (((dn + 4) % 7) + 7) % 7;
}

function partsOf(dn) {
  const d = new Date(dn * DAY_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
}

function toYmd(dn) {
  const p = partsOf(dn);
  return p.y + '-' + String(p.m).padStart(2, '0') + '-' + String(p.d).padStart(2, '0');
}

function fromYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const dn = dayNumber(+m[1], +m[2], +m[3]);
  return toYmd(dn) === m[0] ? dn : null;
}

// n = -1 means the last one in the month.
function nthWeekday(y, m, wd, n) {
  if (n > 0) {
    const first = dayNumber(y, m, 1);
    return first + ((wd - weekday(first) + 7) % 7) + (n - 1) * 7;
  }
  const last = dayNumber(y, m + 1, 1) - 1;
  return last - ((weekday(last) - wd + 7) % 7);
}

// Anonymous Gregorian (Meeus/Jones/Butcher) algorithm.
function easterSunday(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
  const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return dayNumber(y, Math.floor((h + l - 7 * m + 114) / 31), ((h + l - 7 * m + 114) % 31) + 1);
}

// A weekend New Year's/Christmas/Boxing Day keeps its own date (no delivery) and adds a weekday substitute.
function gbEawHolidays(y) {
  const easter = easterSunday(y);
  const out = [
    easter - 2,
    easter + 1,
    nthWeekday(y, 5, 1, 1),
    nthWeekday(y, 5, 1, -1),
    nthWeekday(y, 8, 1, -1)
  ];
  [dayNumber(y, 1, 1), dayNumber(y, 12, 25), dayNumber(y, 12, 26)].forEach(function (own) {
    let sub = own;
    while (weekday(sub) === 0 || weekday(sub) === 6 || out.indexOf(sub) !== -1) sub += 1;
    out.push(own);
    if (sub !== own) out.push(sub);
  });
  return out;
}

// Saturday holidays are observed the Friday before, Sunday ones the Monday after; the date itself still counts.
function usFederalHolidays(y) {
  const fixed = [dayNumber(y, 1, 1), dayNumber(y, 6, 19), dayNumber(y, 7, 4), dayNumber(y, 11, 11), dayNumber(y, 12, 25)];
  const out = [
    nthWeekday(y, 1, 1, 3),
    nthWeekday(y, 2, 1, 3),
    nthWeekday(y, 5, 1, -1),
    nthWeekday(y, 9, 1, 1),
    nthWeekday(y, 10, 1, 2),
    nthWeekday(y, 11, 4, 4)
  ];
  fixed.forEach(function (own) {
    out.push(own);
    if (weekday(own) === 6) out.push(own - 1);
    if (weekday(own) === 0) out.push(own + 1);
  });
  return out;
}

const CALENDARS = { 'GB-EAW': gbEawHolidays, US: usFederalHolidays, none: function () { return []; } };

// Neighbouring years too, so a December estimate sees January's holidays.
function holidaySet(calendar, extra, year) {
  const set = new Set();
  const fn = CALENDARS[calendar];
  for (let y = year - 1; y <= year + 1; y++) fn(y).forEach(function (dn) { set.add(dn); });
  (extra || []).forEach(function (s) { const dn = fromYmd(s); if (dn !== null) set.add(dn); });
  return set;
}

function isWholeDays(n) {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n < 100;
}

function parseCutoff(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
  if (!m || +m[1] > 23 || +m[2] > 59) return null;
  return +m[1] * 60 + +m[2];
}

function validTimezone(tz) {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return typeof tz === 'string' && tz.length > 0;
  } catch (e) {
    return false;
  }
}

function validClosure(c) {
  return c && fromYmd(c.date) !== null &&
    (c.productTypes === undefined || Array.isArray(c.productTypes)) &&
    (c.exceptProductTypes === undefined || Array.isArray(c.exceptProductTypes));
}

function isStringList(list) {
  return Array.isArray(list) && list.every(function (x) { return typeof x === 'string' && x.length > 0; });
}

function validMethod(method) {
  const t = method && method.transit;
  return t && typeof t === 'object' && isWholeDays(t['*']) &&
    Object.keys(t).every(function (r) { return isWholeDays(t[r]); }) &&
    (method.checkoutTitles === undefined || isStringList(method.checkoutTitles));
}

// Ops' per-type table: { terracotta: { cutoff: '23:59', makeDays: 2, enabled: true }, ... }.
function validProductTypes(types) {
  return types && typeof types === 'object' && Object.keys(types).every(function (k) {
    const t = types[k];
    return t && parseCutoff(t.cutoff) !== null && typeof t.enabled === 'boolean' &&
      (t.makeDays === undefined || t.makeDays === null || (isWholeDays(t.makeDays) && t.makeDays >= 1));
  });
}

function validateRules(rules) {
  if (!rules || typeof rules !== 'object' || rules.version !== 1) return false;
  if (rules.market !== 'GB' && rules.market !== 'US') return false;
  if (!validTimezone(rules.timezone) || !validProductTypes(rules.productTypes)) return false;
  if (!CALENDARS[rules.productionHolidays] || !CALENDARS[rules.deliveryHolidays]) return false;
  const wds = rules.deliveryWeekdays;
  if (!Array.isArray(wds) || !wds.length || !wds.every(function (w) { return Number.isInteger(w) && w >= 0 && w <= 6; })) return false;
  if (rules.extraDays !== undefined && !isWholeDays(rules.extraDays)) return false;
  if (rules.extraHolidays !== undefined && (!Array.isArray(rules.extraHolidays) || !rules.extraHolidays.every(function (s) { return fromYmd(s) !== null; }))) return false;
  if (rules.closures !== undefined && (!Array.isArray(rules.closures) || !rules.closures.every(validClosure))) return false;
  if (!rules.methods || typeof rules.methods !== 'object' || !Object.keys(rules.methods).length) return false;
  if (!Object.keys(rules.methods).every(function (k) { return validMethod(rules.methods[k]); })) return false;
  if (rules.deliveryCountries !== undefined && !isStringList(rules.deliveryCountries)) return false;
  return rules.addonProductTypes === undefined || isStringList(rules.addonProductTypes);
}

// Countries the transit days are true for; anywhere else gets no date (e.g. the Channel Islands in the GB rate zone).
function deliversTo(rules, region) {
  if (!region || !rules.deliveryCountries) return true;
  const country = String(region).toUpperCase().split('-')[0];
  return rules.deliveryCountries.some(function (c) { return c.toUpperCase() === country; });
}

// Hidden option/add-on products ride along with a real item, so they never set or block a date.
function isAddon(rules, productType) {
  return validateRules(rules) && typeIn(rules.addonProductTypes, productType);
}

// The engine method a checkout shipping rate stands for, by its exact title; unknown rates (e.g. influencer) get none.
function methodForRate(rules, title) {
  if (!validateRules(rules)) return null;
  const t = norm(title);
  return Object.keys(rules.methods).find(function (k) {
    return (rules.methods[k].checkoutTitles || []).some(function (x) { return norm(x) === t; });
  }) || null;
}

function norm(s) {
  return String(s || '').trim().toLowerCase();
}

function typeIn(list, productType) {
  return (list || []).some(function (t) { return norm(t) === norm(productType); });
}

function typeConfig(rules, productType) {
  const key = Object.keys(rules.productTypes).find(function (k) { return norm(k) === norm(productType); });
  return key === undefined ? null : rules.productTypes[key];
}

// The range-by-range rollout switch, set per product type by ops.
function isEnabled(rules, productType) {
  if (!validateRules(rules)) return false;
  const cfg = typeConfig(rules, productType);
  return !!cfg && cfg.enabled === true;
}

function makeDaysOf(rules, item) {
  if (item.makeDays !== undefined && item.makeDays !== null) return item.makeDays;
  const cfg = typeConfig(rules, item.productType);
  return cfg && cfg.makeDays;
}

function transitDays(rules, method, region) {
  const m = rules.methods[method];
  if (!m) return null;
  const t = m.transit, r = String(region || '').toUpperCase();
  if (r && isWholeDays(t[r])) return t[r];
  const country = r.split('-')[0];
  if (country && isWholeDays(t[country])) return t[country];
  return t['*'];
}

function wallClock(now, timezone) {
  const parts = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(now).forEach(function (p) { parts[p.type] = p.value; });
  return { dn: dayNumber(+parts.year, +parts.month, +parts.day), minutes: (+parts.hour % 24) * 60 + +parts.minute };
}

function nextMatching(from, n, ok) {
  let d = from;
  for (let i = 0; i < n; i++) {
    do { d += 1; } while (!ok(d));
  }
  return d;
}

// The type's cut-off decides the order day; production starts the next working day; dispatch is the last production day.
function dispatchFor(rules, item, wall, prodHolidays) {
  const closed = new Set(prodHolidays);
  (rules.closures || []).forEach(function (c) {
    const applies = c.productTypes ? typeIn(c.productTypes, item.productType)
      : !(c.exceptProductTypes && typeIn(c.exceptProductTypes, item.productType));
    if (applies) closed.add(fromYmd(c.date));
  });
  const working = function (d) { const w = weekday(d); return w >= 1 && w <= 5 && !closed.has(d); };
  const orderDay = wall.minutes < parseCutoff(typeConfig(rules, item.productType).cutoff) ? wall.dn : wall.dn + 1;
  const start = nextMatching(orderDay, 1, working);
  const days = makeDaysOf(rules, item) + (item.extraDays || 0) + (rules.extraDays || 0);
  return { start: start, dispatch: nextMatching(start, days - 1, working) };
}

function validItems(rules, items) {
  return Array.isArray(items) && items.length > 0 && items.every(function (it) {
    if (!it || !isEnabled(rules, it.productType)) return false;
    const days = makeDaysOf(rules, it);
    return isWholeDays(days) && days >= 1 && (it.extraDays === undefined || isWholeDays(it.extraDays));
  });
}

function estimateAt(rules, items, wall, method, region) {
  const year = partsOf(wall.dn).y;
  const prodHolidays = holidaySet(rules.productionHolidays, rules.extraHolidays, year);
  let slowest = null;
  items.forEach(function (it) {
    const r = dispatchFor(rules, it, wall, prodHolidays);
    if (!slowest || r.dispatch > slowest.dispatch) slowest = r;
  });
  let delivery = null;
  if (method) {
    const transit = transitDays(rules, method, region);
    if (transit === null) return null;
    const delHolidays = holidaySet(rules.deliveryHolidays, rules.extraHolidays, year);
    const delivers = function (d) { return rules.deliveryWeekdays.indexOf(weekday(d)) !== -1 && !delHolidays.has(d); };
    delivery = nextMatching(slowest.dispatch, transit, delivers);
  }
  return {
    productionStart: toYmd(slowest.start),
    dispatchDate: toYmd(slowest.dispatch),
    deliveryDate: delivery === null ? null : toYmd(delivery),
    engineVersion: ENGINE_VERSION
  };
}

// items: [{ productType, makeDays? (product override), extraDays? }] without add-on lines; opts: { now, method?, region? }.
function estimate(rules, items, opts) {
  if (!validateRules(rules) || !validItems(rules, items)) return null;
  const o = opts || {};
  if (!(o.now instanceof Date) || isNaN(o.now.getTime()) || !deliversTo(rules, o.region)) return null;
  return estimateAt(rules, items, wallClock(o.now, rules.timezone), o.method, o.region);
}

// "As soon as": the earliest-arriving method; ties go to the method listed first in the rules.
function fastest(rules, items, opts) {
  if (!validateRules(rules)) return null;
  let best = null;
  Object.keys(rules.methods).forEach(function (method) {
    const r = estimate(rules, items, Object.assign({}, opts, { method: method }));
    if (r && (!best || r.deliveryDate < best.deliveryDate)) best = Object.assign({ method: method }, r);
  });
  return best;
}

// Latest day an order placed before the cut-off still arrives by opts.deliverBy (e.g. a Christmas deadline).
function lastOrderDate(rules, items, opts) {
  if (!validateRules(rules) || !validItems(rules, items)) return null;
  const o = opts || {};
  const target = fromYmd(o.deliverBy);
  if (target === null || !o.method) return null;
  // Just before the earliest cut-off in the group; a 00:00 cut-off has no 'before', so stay at minute 0.
  const cutoffs = items.map(function (it) { return parseCutoff(typeConfig(rules, it.productType).cutoff); });
  const minutes = Math.max(Math.min.apply(null, cutoffs) - 1, 0);
  for (let k = 0; k <= 90; k++) {
    const r = estimateAt(rules, items, { dn: target - k, minutes: minutes }, o.method, o.region);
    if (!r) return null;
    if (fromYmd(r.deliveryDate) <= target) return toYmd(target - k);
  }
  return null;
}

const SEASON_LEAD_DAYS = 14;
const COUNTDOWN_MS = DAY_MS;

function instant(s) {
  if (s === null || s === undefined || s === '' || s === 0) return null;
  const t = new Date(s).getTime();
  return isNaN(t) ? null : t;
}

function validSeason(season) {
  return season && typeof season.occasion === 'string' && season.occasion &&
    fromYmd(season.occasionDate) !== null && Array.isArray(season.deadlines);
}

// Ops' last-order time for one method across the group: the earliest row of any covered item binds; uncovered items don't lift it.
function groupDeadline(season, items, method, region) {
  let earliest = null;
  for (const it of items) {
    let best = null;
    season.deadlines.forEach(function (d) {
      const t = instant(d.lastOrderAt);
      if (t === null || d.method !== method || norm(d.productType) !== norm(it.productType)) return;
      if (d.variant && norm(d.variant) !== norm(it.variant)) return;
      if (region && d.region && d.region !== region && d.region !== region.split('-')[0]) return;
      if (best === null || t < best) best = t;
    });
    if (best !== null && (earliest === null || best < earliest)) earliest = best;
  }
  return earliest;
}

// Stage 'standard' | 'express' while ops' deadline is open, 'closed' until the occasion; no opts.region = the earliest region.
function seasonState(rules, seasons, items, opts) {
  if (!validateRules(rules) || !Array.isArray(seasons) || !Array.isArray(items) || !items.length) return null;
  const o = opts || {};
  if (!(o.now instanceof Date) || isNaN(o.now.getTime())) return null;
  const now = o.now.getTime();
  const today = wallClock(o.now, rules.timezone).dn;
  const live = seasons.filter(validSeason).filter(function (s) { return fromYmd(s.occasionDate) >= today; })
    .sort(function (a, b) { return fromYmd(a.occasionDate) - fromYmd(b.occasionDate); });
  for (const s of live) {
    const standard = groupDeadline(s, items, 'standard', o.region);
    const express = groupDeadline(s, items, 'express', o.region);
    if (standard === null && express === null) continue;
    const first = Math.min.apply(null, [standard, express].filter(function (t) { return t !== null; }));
    const from = fromYmd(s.showFrom);
    const showFrom = from !== null ? from : wallClock(new Date(first), rules.timezone).dn - SEASON_LEAD_DAYS;
    if (today < showFrom) return null;
    const deadlines = {};
    Object.keys(rules.methods).forEach(function (m) { deadlines[m] = groupDeadline(s, items, m, o.region); });
    let stage = 'closed', deadline = null;
    if (standard !== null && now < standard) { stage = 'standard'; deadline = standard; }
    else if (express !== null && now < express) { stage = 'express'; deadline = express; }
    return {
      occasion: s.occasion,
      occasionDate: s.occasionDate,
      stage: stage,
      deadline: deadline === null ? null : new Date(deadline).toISOString(),
      deadlineDate: deadline === null ? null : toYmd(wallClock(new Date(deadline), rules.timezone).dn),
      countdownMs: deadline !== null && deadline - now <= COUNTDOWN_MS ? deadline - now : null,
      deadlines: deadlines,
      at: now
    };
  }
  return null;
}

// Once a method's own deadline has passed, its date on or before the occasion would imply it still arrives in time, so it isn't shown.
function hidesForSeason(state, method, ymd) {
  if (!state || !ymd || ymd > state.occasionDate) return false;
  const deadline = state.deadlines && state.deadlines[method];
  // A method with no usable deadline of its own follows the season: hidden once every supplied deadline has passed.
  if (deadline === null || deadline === undefined) return state.stage === 'closed';
  return state.at >= deadline;
}

// The words for a season state, as segments so every surface bolds the same parts: [{ text, strong }].
function seasonMessage(rules, state) {
  if (!state || (state.stage !== 'standard' && state.stage !== 'express')) return null;
  const by = state.stage === 'express' ? ' with Express' : '';
  if (state.countdownMs !== null) {
    const mins = Math.max(1, Math.ceil(state.countdownMs / 60000));
    const left = Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
    return [{ text: 'Order within ', strong: false }, { text: left, strong: true }, { text: by + ' for ' + state.occasion, strong: false }];
  }
  return [{ text: 'Order by ', strong: false }, { text: formatDate(rules, state.deadlineDate, 'long'), strong: true }, { text: by + ' for ' + state.occasion, strong: false }];
}

// Flags ops' last-order dates the engine disagrees with: 'late' arrives after the occasion, 'early' undersells (see latestInTime).
function checkSeason(rules, season) {
  if (!validateRules(rules) || !validSeason(season)) return null;
  const occasion = fromYmd(season.occasionDate);
  const problems = [];
  season.deadlines.forEach(function (d) {
    const cfg = typeConfig(rules, d.productType);
    const t = instant(d.lastOrderAt);
    if (!cfg || !cfg.makeDays || t === null || !rules.methods[d.method]) {
      problems.push({ kind: 'unknown', productType: d.productType, method: d.method, region: d.region });
      return;
    }
    // Per-variant extra make days aren't known here, so a variant row would be checked against the wrong speed.
    if (d.variant) {
      problems.push({ kind: 'variant', productType: d.productType, variant: d.variant, method: d.method, region: d.region });
      return;
    }
    // Checked whether or not the type is switched on yet, so ops can fix dates before a launch.
    const types = Object.assign({}, rules.productTypes);
    types[d.productType] = Object.assign({}, cfg, { enabled: true });
    const typeRules = Object.assign({}, rules, { productTypes: types });
    const items = [{ productType: d.productType }];
    const r = estimate(typeRules, items, { now: new Date(t - 60000), method: d.method, region: d.region });
    const latest = lastOrderDate(typeRules, items, { deliverBy: season.occasionDate, method: d.method, region: d.region });
    const deadlineDay = toYmd(wallClock(new Date(t), rules.timezone).dn);
    if (r && fromYmd(r.deliveryDate) > occasion) {
      problems.push({ kind: 'late', productType: d.productType, method: d.method, region: d.region, deadline: deadlineDay, arrives: r.deliveryDate, latestInTime: latest });
    } else if (latest && fromYmd(latest) > fromYmd(deadlineDay)) {
      problems.push({ kind: 'early', productType: d.productType, method: d.method, region: d.region, deadline: deadlineDay, latestInTime: latest });
    }
  });
  return problems;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// 'property' matches the Liquid '%A, %d %B' Dispatch date value it replaces, so order parsing is unchanged.
function formatDate(rules, ymd, style) {
  const dn = fromYmd(ymd);
  if (dn === null || !rules) return '';
  const p = partsOf(dn), day = DAYS[weekday(dn)], month = MONTHS[p.m - 1];
  if (style === 'property') return day + ', ' + String(p.d).padStart(2, '0') + ' ' + month;
  if (style === 'short') {
    return rules.market === 'US'
      ? day.slice(0, 3) + ', ' + month.slice(0, 3) + ' ' + p.d
      : day.slice(0, 3) + ' ' + p.d + ' ' + month.slice(0, 3);
  }
  return rules.market === 'US' ? day + ', ' + month + ' ' + p.d : day + ', ' + p.d + ' ' + month;
}

window.LfDeliveryDates = { ENGINE_VERSION, toYmd, fromYmd, validateRules, isAddon, methodForRate, isEnabled, estimate, fastest, lastOrderDate, seasonState, hidesForSeason, seasonMessage, checkSeason, formatDate };
})();
